import { useEffect, useState } from "react";
import { apiGet } from "./client.js";
import { useApi, type ApiState } from "./useApi.js";

/**
 * NX-2635 (E8.9) — the narrative service: the only place the Project-detail narrative panel learns
 * how to reach the API, mirroring `assessmentService.ts`'s injected-hook convention (AC1). The panel
 * takes a `NarrativeService` and never calls `apiGet`/`fetch` itself.
 *
 * SHAPES MIRROR `apps/api/src/narrative.ts` (E7.6, NX-2639) — mirrored, not imported: that route
 * lives in `apps/api`, a Node workspace this browser bundle must never pull in (crypto/pg), the same
 * reason `assessmentService.ts`/`projectMetrics.ts` mirror rather than import. NX-2639 is an OPEN PR
 * at the time of this story (not `main`) — this file was hand-checked line-by-line against that
 * PR's `apps/api/src/narrative.ts` rather than against `main`, since the route does not exist on
 * `main` yet. Re-check this mirror once NX-2639 actually merges, the same follow-up
 * `NarrativeSeam.tsx` (NX-2633) already flags for its own (different) narrative seam.
 *
 * `packages/ai/src/narrative.ts` (E6.4, NX-2614, ON `main`) is the deeper contract this ultimately
 * rests on: `text` carries inline `[[fact:<citationId>]]` markers (packages/ai/src/grounding.ts's
 * `CITATION_RE`) and `citations` is the flat list of every id the model actually cited. Never a
 * second citation-marker syntax invented here.
 */

/** Mirrors `NarrativeStatus` (apps/api/src/narrative.ts). `"opening"` (NX-2701) is a real, cited
 *  opening statement composed deterministically from evidence — never model output — which is
 *  exactly why `NarrativePanel.tsx` renders it WITHOUT the "AI" tag, unlike `"generated"`. */
export type NarrativeStatus = "generated" | "opening" | "refused" | "unavailable";

/** Mirrors `WhatChangedView` (apps/api/src/narrative.ts). `reason` is populated whenever
 *  `status !== "generated"` and is relayed VERBATIM by the panel — never re-interpreted or
 *  replaced with a client-composed sentence (the same "relay the server's own N/A reason" rule
 *  `EstimationPanel.tsx` already follows for `coverage.reason`). That is deliberate here: the ONLY
 *  way this client can tell "no prior visit to compare" (AC3/TC3) apart from "thin basis" (AC4/TC2)
 *  or a genuine "nothing changed" is the server's own reason text — this module invents none of
 *  those three sentences itself, and must never collapse them into one client-composed message. */
export interface WhatChangedView {
  readonly status: NarrativeStatus;
  readonly reason: string | null;
  readonly text: string | null;
  readonly citations: readonly number[];
}

/** Mirrors `ProjectNarrativeView` (apps/api/src/narrative.ts). */
export interface ProjectNarrativeView {
  readonly projectId: string;
  readonly status: NarrativeStatus;
  readonly reason: string | null;
  readonly text: string | null;
  readonly citations: readonly number[];
  readonly whatChanged: WhatChangedView;
}

export interface NarrativeService {
  /** `GET /projects/:id/narrative` (E7.6/NX-2639). A 403 (AC6 — role withheld) or any other
   *  transport error surfaces via `ApiState`'s `"error"` branch exactly like every other `useApi`
   *  consumer; the panel is responsible for rendering that honestly, never retrying with a guessed
   *  fallback. */
  useNarrative(projectId: string): ApiState<ProjectNarrativeView>;
}

export const liveNarrativeService: NarrativeService = {
  useNarrative: (projectId) => useApi<ProjectNarrativeView>(`/projects/${projectId}/narrative`),
};

// ── Citation -> provenance resolution (AC2) ─────────────────────────────────────────────────────

/** The one field set a `ProvenancePopover` trigger needs (components.tsx) — a local mirror of
 *  `EvidenceItem`'s source/method/confidence/effectiveAt slice (apps/api/src/evidence.ts),
 *  independently of `EvidenceContent.tsx`'s own copy (routes/ never imports api/, and this module
 *  stays a peer of EvidenceContent rather than a dependent of it). */
export interface CitationProvenance {
  readonly source: string;
  readonly method: string;
  readonly confidence: number;
  readonly effectiveAt: string;
}

export type CitationLineageState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly provenance: CitationProvenance }
  /** Honest failure — a citation id that does not resolve (404/network/etc). Never fabricated
   *  source/method/confidence; the panel renders this as an honest "unresolved" trigger, still
   *  focusable (TC1), never silently dropped. */
  | { readonly status: "error" };

/**
 * Resolves each citation id in `factIds` to its provenance via `GET /projects/:id/citation/:eventId`
 * (evidence.ts, NX-2700) — never a second lineage mechanism. That route resolves a narrative citation
 * id CORRECTLY: a citation id is a raw `events.id` (`packages/ai/src/grounding.ts`'s `factFromMetric`
 * doc comment), a DIFFERENT id space than `GET /projects/:id/lineage/:factId`'s `work_item_state`-
 * keyed `factId` — NX-2700 gave the two spaces their own route (`/citation/:eventId` vs
 * `/lineage/:factId`) so a caller can no longer point a narrative citation at the wrong resolver.
 * Fetches the bounded, deduplicated id set once (grounding.ts caps a narrative's retrieved facts at
 * `DEFAULT_FACT_CAP` = 40, so this is a small, one-shot batch, not unbounded fan-out) and keeps
 * fetching out of the render path: `apiGet` directly, not `useApi` — the id set is dynamic (however
 * many `[[fact:<id>]]` markers the generated text actually contains), so it cannot be looped through
 * a fixed number of `useApi` hook calls (Rules of Hooks).
 */
export function useCitationLineages(
  projectId: string,
  factIds: readonly number[],
  token: string | null,
): ReadonlyMap<number, CitationLineageState> {
  const key = [...new Set(factIds)].sort((a, b) => a - b).join(",");
  const [state, setState] = useState<ReadonlyMap<number, CitationLineageState>>(new Map());

  useEffect(() => {
    const ids = key === "" ? [] : key.split(",").map(Number);
    if (ids.length === 0) {
      setState(new Map());
      return;
    }
    let cancelled = false;
    setState(new Map(ids.map((id) => [id, { status: "loading" } as const])));
    type Entry = readonly [number, CitationLineageState];
    void Promise.all<Entry>(
      ids.map(async (id): Promise<Entry> => {
        try {
          const envelope = await apiGet<CitationProvenance>(
            `/projects/${projectId}/citation/${id}`,
            {
              token,
            },
          );
          return [id, { status: "ready", provenance: envelope.data }];
        } catch {
          // Honest failure (404/network/etc) — never fabricated provenance.
          return [id, { status: "error" }];
        }
      }),
    ).then((entries) => {
      if (!cancelled) setState(new Map(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, key, token]);

  return state;
}
